import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { Banner, Button, Input, Table } from "@cinesys/ui/components";
import { SPOTTER_STRINGS } from "../../strings";
import { GamebookPlay } from "../../gamebook/service";
import {
  computeScoredIndices,
  flagsCellContent,
  playCellContent,
  situationCellContent,
} from "../../gamebook/gamebookCellContent";

/**
 * The full-page anchor editor's gamebook band (Option A: "stage over sheet") — a full-width,
 * four-column, always-scrollable play list, replacing `GamebookPickerTable`'s cramped ±4-window
 * picker for THIS surface only (`GamebookPickerTable` itself is untouched and still serves
 * Phase A's start picker, unchanged).
 *
 * COLUMNS: UID 105 / Situation 240 / What happened 1fr / Flags 130 — `Off ⏵ Def` and `Score` are
 * deliberately NOT here; both belong in a row-detail expansion this component doesn't add. 491px
 * of play text at 1400px is why every play in the fixture fits on one line.
 *
 * REUSE, NOT A SECOND SET: `situationCellContent`/`playCellContent`/`flagsCellContent` are the
 * SAME functions `../gamebook/GamebookScreen.tsx` renders, imported from
 * `../../gamebook/gamebookCellContent.tsx`. Each cell's own wrapper below (`SitCellWrap`/
 * `PlayCellWrap`/`FlagsCellWrap`) copies GamebookScreen's own `.q`/`.dd`/`.yd` CSS verbatim —
 * styled-components scopes those nested selectors to their own ancestor, so the CONTENT is
 * shared but each table supplies its own (here: unchanged) styling. `Fl` (the flag pill) is
 * reused unrestyled too — the Option A mockup draws a true pill (`border-radius: 999px`) where
 * the existing `Fl` is a smaller rounded chip; kept as-is rather than forking a second flag-pill
 * look, since restyling `Fl` would also restyle GamebookScreen's own (untouched) flag pills. A
 * documented, deliberate deviation from the mockup, not an oversight.
 *
 * SPOTTER'S PICK, CENTRED: `scrollToUid` (computed by the caller via `spotterPickScroll.ts`'s
 * `resolveScrollTargetUid`) drives a `scrollIntoView({ block: "center" })` on that row's own DOM
 * node, captured via `Table`'s `domRef` escape hatch (same pattern `GamebookPickerTable.tsx`
 * already uses for its keyboard affordance) — re-run on every `scrollToUid` change (mount AND
 * anchor switch, since this table itself never remounts between anchors: `plays` is the same
 * whole-game list throughout). No manual pixel math: the browser's own `scrollIntoView` already
 * clamps at either end of the list when true centering isn't possible (Option A's "8 rows
 * visible" note).
 *
 * MANUAL UID ESCAPE HATCH: the "Play not listed?" input + Set button live in this table's own
 * header (`GbTop`, right-aligned), not down in `AnchorStage.tsx`'s meta panel — moved here
 * (Addison) since it's really an alternate way to pick a row in THIS table, not part of the
 * video/neighbor-nav stage. State/validation/the actual commit stay owned by
 * `AnchorConfirmContent.tsx` (same place `onSelect` below already commits a row pick from) —
 * this component just renders the controlled input and calls back on submit.
 */

const COLUMN_WIDTHS = ["105px", "240px", "1fr", "130px"];

/** `min-height` is a real floor, not `0` (Addison: "the gamebook has to have a minimum height
 *  because it should always show even on a small screen") — enough for the header + column heads
 *  + a handful of rows, so a short viewport shrinks `AnchorStage.tsx`'s fixed-height `StageRow`
 *  (which now tolerates that, see its own `min-height`) before this ever disappears. */
const GAMEBOOK_MIN_HEIGHT_PX = 240;

const Wrap = styled.div`
  border: 1px solid var(--spotterLine);
  border-radius: 10px;
  overflow: hidden;
  flex: 1;
  min-height: ${GAMEBOOK_MIN_HEIGHT_PX}px;
  display: flex;
  flex-direction: column;
`;

/** `5px` top/bottom (Addison: "the header's just rendering too big with the input in there") —
 *  down from `11px`; the manual-UID `Input`'s own explicit `height` (see `ManualEntry` below) is
 *  what actually caps this row's height now, not padding around a taller default input size. */
const GbTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 5px 14px;
  border-bottom: 1px solid var(--spotterLine);
  flex: none;
`;

const GbHeading = styled.span`
  font-size: 12.5px;
  font-weight: 600;
  color: var(--spotterInk);
`;

const GbSub = styled.span`
  font-size: 11px;
  color: var(--spotterInkFaint);
`;

const GbTopSpacer = styled.div`
  flex: 1;
`;

/** Plain inline label, not `Input`'s own `label` prop — that prop renders a floating label
 *  ABOVE the field (`Wrapper`'s `flex-basis: 100%` in `@cinesys/ui`'s `Input.tsx`), which is a
 *  taller two-row shape than a compact header row has room for and doesn't vertically center
 *  against the button next to it. This sits to the input's LEFT instead, all one row. */
const ManualLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: var(--spotterInk);
  white-space: nowrap;
`;

const ManualEntry = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
`;

/** Column labels, matching `GamebookScreen.tsx`'s own `Colhead` treatment (grid aligned to the
 *  same `COLUMN_WIDTHS`, uppercase mono caption) but non-sticky — a plain sibling of `ScrollArea`
 *  outside the scroll region already stays put without `position: sticky`. */
const Colhead = styled.div`
  display: grid;
  grid-template-columns: ${COLUMN_WIDTHS.join(" ")};
  gap: 10px;
  align-items: center;
  padding: 8px 14px;
  border-bottom: 1px solid var(--spotterLine);
  font-size: 10.5px;
  letter-spacing: 0.13em;
  text-transform: uppercase;
  color: var(--spotterInkFaint);
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 700;
  flex: none;
`;

/** The table's own scroll container — `Table` itself renders plain (no `height`/`maxHeight`
 *  prop), same as `GamebookScreen.tsx`'s desktop grid; this wrapper owns the scrolling instead of
 *  the page (see file doc comment), via ordinary flex sizing rather than Table's own internal
 *  scroll region. */
const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

/** `GamebookScreen.tsx`'s `SitCell`, copied verbatim (see file doc comment on why a copy, not an
 *  import — the CONTENT is shared, the wrapper isn't). */
const SitCellWrap = styled.span`
  font-size: 12.5px;
  color: var(--spotterInkSoft);
  font-variant-numeric: tabular-nums;

  .q {
    font-family: ui-monospace, Menlo, monospace;
    font-weight: 700;
    color: var(--spotterInk);
    font-size: 12px;
    background: var(--spotterCodeBg);
    padding: 2px 6px;
    border-radius: 5px;
    margin-right: 7px;
  }

  .dd {
    color: var(--spotterInk);
    font-weight: 600;
  }
`;

/** `GamebookScreen.tsx`'s `PlayCell`, copied verbatim. Wraps, never truncates — a play
 *  description should never be silently cut off by an ellipsis, even when a parenthetical
 *  tackler credit runs the text past one line. */
const PlayCellWrap = styled.span`
  font-size: 13px;
  color: var(--gray300);
  white-space: normal;
  overflow-wrap: break-word;

  .yd {
    font-family: ui-monospace, Menlo, monospace;
    font-weight: 700;
    font-size: 13.5px;
    margin-right: 8px;
  }

  .yd.pos {
    color: var(--spotterOk);
  }

  .yd.neg {
    color: var(--spotterDanger);
  }

  .yd.zero {
    color: var(--spotterInkFaint);
  }
`;

/** `flex-wrap` (unlike `GamebookScreen.tsx`'s own `FlagsCell`, which never needs it — that
 *  column has no "Spotter's pick" badge sharing it) — a flagged AND suggested play puts a real
 *  flag pill next to `PickTag` in the same 130px column; wrapping to a second line beats
 *  clipping either one. */
const FlagsCellWrap = styled.span`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-start;
  align-items: center;
  row-gap: 4px;
`;

const UidCellWrap = styled.span`
  font-family: ui-monospace, Menlo, monospace;
  font-weight: 700;
  font-size: 12.5px;
  color: var(--spotterAccent);
`;

const PickTag = styled.span`
  font-size: 9.5px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  background: var(--spotterAccent);
  color: var(--spotterPaper);
  white-space: nowrap;
`;

export interface AnchorGamebookTableProps {
  plays: GamebookPlay[];
  gameLabel: string;
  suggestedUid: string;
  scrollToUid: string | null;
  locked: boolean;
  onSelect: (play: GamebookPlay) => void;
  /** The manual-UID escape hatch's own controlled state/handlers — owned by the caller
   *  (`AnchorConfirmContent.tsx`), see file doc comment. Omitted entirely (not just hidden) once
   *  `locked` — there's no play left to manually pick at that point. */
  manualUid: {
    value: string;
    invalid: boolean;
    onChange: (value: string) => void;
    onSubmit: () => void;
  };
}

const AnchorGamebookTable = ({
  plays,
  gameLabel,
  suggestedUid,
  scrollToUid,
  locked,
  onSelect,
  manualUid,
}: AnchorGamebookTableProps) => {
  const t = SPOTTER_STRINGS.filmsGround.phaseB;
  const scoredIdx = computeScoredIndices(plays);
  const rowRefs = useRef<Map<string, HTMLElement>>(new Map());

  // Centre Spotter's pick (or the resolved-neighbour fallback the caller already computed) — on
  // mount AND on every anchor switch, since `scrollToUid` changes but this table never remounts
  // (see file doc comment).
  useEffect(() => {
    if (!scrollToUid) {
      return;
    }
    const el = rowRefs.current.get(scrollToUid);
    el?.scrollIntoView({ block: "center" });
  }, [scrollToUid]);

  return (
    <Wrap>
      <GbTop>
        <GbHeading>{t.gamebookHeading}</GbHeading>
        <GbSub>{gameLabel}</GbSub>
        {!locked && (
          <>
            <GbTopSpacer />
            <ManualEntry>
              <ManualLabel>{t.manualUidLabel}</ManualLabel>
              <Input
                value={manualUid.value}
                onChange={manualUid.onChange}
                placeholder={t.manualUidPlaceholder}
                invalid={manualUid.invalid}
                width={70}
                height={30}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    manualUid.onSubmit();
                  }
                }}
              />
              <Button
                label={t.manualUidSet}
                type="default"
                onClick={manualUid.onSubmit}
              />
            </ManualEntry>
          </>
        )}
      </GbTop>
      {!locked && manualUid.invalid && (
        <Banner error={t.manualUidInvalid} padBottom />
      )}
      <Colhead>
        <div>{t.gamebookColumnUid}</div>
        <div>{t.gamebookColumnSituation}</div>
        <div>{t.gamebookColumnWhatHappened}</div>
        <div>{t.gamebookColumnFlags}</div>
      </Colhead>
      <ScrollArea>
        <Table
          columns={COLUMN_WIDTHS.map((width) => ({ width }))}
          columnGrid
          rowHeight={38}
          rowPadding="7px 14px"
          rows={plays.map((play, i) => {
            const isSuggested = play.uid === suggestedUid;
            const handleSelect = () => {
              if (!locked) {
                onSelect(play);
              }
            };
            return {
              id: play.uid,
              onClick: locked ? undefined : handleSelect,
              style: {
                cursor: locked ? "default" : "pointer",
                ...(isSuggested
                  ? {
                      boxShadow: "inset 2px 0 0 var(--spotterAccent)",
                      background: "var(--spotterAccentSoft)",
                    }
                  : undefined),
              },
              // Keyboard access + the scroll-centring ref (see `GamebookPickerTable.tsx`'s own
              // doc comment on why `Table` needs this escape hatch for row keyboard support).
              domRef: (el: HTMLElement | null) => {
                if (!el) {
                  rowRefs.current.delete(play.uid);
                  return;
                }
                rowRefs.current.set(play.uid, el);
                if (locked) {
                  return;
                }
                el.tabIndex = 0;
                el.setAttribute("role", "button");
                el.setAttribute(
                  "aria-label",
                  `Pick play ${play.uid}: ${play.mechanicalSummary ?? ""}`
                );
                el.onkeydown = (e: KeyboardEvent) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleSelect();
                  }
                };
              },
              element: () => (
                <>
                  <UidCellWrap>{play.uid}</UidCellWrap>
                  <SitCellWrap>{situationCellContent(play)}</SitCellWrap>
                  <PlayCellWrap>{playCellContent(play)}</PlayCellWrap>
                  <FlagsCellWrap>
                    {flagsCellContent(scoredIdx.has(i), play)}
                    {isSuggested && <PickTag>{t.gamebookPickTag}</PickTag>}
                  </FlagsCellWrap>
                </>
              ),
            };
          })}
        />
      </ScrollArea>
    </Wrap>
  );
};

export default AnchorGamebookTable;
